/**
 * Meta 픽셀 고급 매칭 유틸리티
 * 사용자 정보를 기반으로 Meta 픽셀을 초기화하고 고급 매칭을 설정합니다.
 */

// Meta 픽셀 ID
const META_PIXEL_ID = '263515730979128';

/**
 * SHA-256 해시 함수
 * @param {string} str - 해시할 문자열
 * @returns {Promise<string>} - SHA-256 해시된 문자열
 */
const sha256 = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * 이메일을 정규화 (소문자, 공백 제거)
 * @param {string} email - 이메일 주소
 * @returns {string} - 정규화된 이메일
 */
const normalizeEmail = (email) => {
  if (!email) return null;
  return email.toLowerCase().trim();
};

/**
 * 이름을 정규화 (소문자, 공백 제거)
 * @param {string} name - 이름
 * @returns {string} - 정규화된 이름
 */
const normalizeName = (name) => {
  if (!name) return null;
  // 이름에서 공백 제거하고 소문자로 변환
  return name.toLowerCase().trim().replace(/\s+/g, '');
};

/**
 * 전화번호를 정규화 (숫자만 추출)
 * @param {string} phone - 전화번호
 * @returns {string} - 정규화된 전화번호 (숫자만)
 */
const normalizePhone = (phone) => {
  if (!phone) return null;
  // 숫자만 추출
  const digits = phone.replace(/\D/g, '');
  // 한국 번호인 경우 국가 코드 추가 (82)
  if (digits.length === 11 && digits.startsWith('0')) {
    return '82' + digits.substring(1);
  }
  if (digits.length === 10) {
    return '82' + digits;
  }
  return digits;
};

/**
 * 사용자 정보를 Meta 픽셀 고급 매칭 형식으로 변환
 * @param {Object} userInfo - 사용자 정보 객체
 * @param {string} userInfo.email - 이메일
 * @param {string} userInfo.name - 이름 (전체 이름)
 * @param {string} userInfo.mobile - 전화번호
 * @returns {Promise<Object>} - 고급 매칭 데이터 객체
 */
export const prepareAdvancedMatching = async (userInfo) => {
  const matchingData = {};

  // 이메일 처리
  if (userInfo && userInfo.email) {
    const normalizedEmail = normalizeEmail(userInfo.email);
    if (normalizedEmail) {
      // Meta 픽셀은 해시되지 않은 소문자 이메일도 허용하지만,
      // 보안을 위해 해시된 값도 함께 전송할 수 있습니다.
      // 여기서는 해시되지 않은 소문자 이메일을 전송 (픽셀이 자동으로 해시)
      matchingData.em = normalizedEmail;
    }
  }

  // 이름 처리 (전체 이름을 이름과 성으로 분리)
  if (userInfo && userInfo.name) {
    const normalizedName = normalizeName(userInfo.name);
    if (normalizedName) {
      // 한국 이름은 보통 2-3글자이므로 첫 글자를 성으로, 나머지를 이름으로 처리
      // 또는 전체를 이름으로 처리
      const nameParts = normalizedName.split(/\s+/);
      if (nameParts.length >= 2) {
        matchingData.ln = nameParts[nameParts.length - 1]; // 마지막 부분을 성으로
        matchingData.fn = nameParts.slice(0, -1).join(''); // 나머지를 이름으로
      } else {
        // 이름이 하나인 경우 전체를 이름으로 처리
        matchingData.fn = normalizedName;
      }
    }
  }

  // 전화번호 처리
  if (userInfo && userInfo.mobile) {
    const normalizedPhone = normalizePhone(userInfo.mobile);
    if (normalizedPhone) {
      matchingData.ph = normalizedPhone;
    }
  }

  return matchingData;
};

/**
 * Meta 픽셀 초기화 (고급 매칭 포함)
 * @param {Object} userInfo - 사용자 정보 객체
 */
export const initMetaPixel = async (userInfo = null) => {
  // fbq 함수가 로드되었는지 확인
  if (typeof window === 'undefined' || !window.fbq) {
    console.warn('Meta 픽셀이 아직 로드되지 않았습니다.');
    return;
  }

  try {
    let matchingData = {};

    // 사용자 정보가 제공된 경우 고급 매칭 데이터 준비
    if (userInfo) {
      matchingData = await prepareAdvancedMatching(userInfo);
    }

    // 고급 매칭 데이터가 있는 경우에만 세 번째 매개변수로 전달
    if (Object.keys(matchingData).length > 0) {
      window.fbq('init', META_PIXEL_ID, matchingData);
      console.log('Meta 픽셀 초기화 완료 (고급 매칭 포함):', matchingData);
    } else {
      // 사용자 정보가 없는 경우 기본 초기화
      window.fbq('init', META_PIXEL_ID);
      console.log('Meta 픽셀 초기화 완료 (기본)');
    }

    // PageView 이벤트 전송
    window.fbq('track', 'PageView');
  } catch (error) {
    console.error('Meta 픽셀 초기화 중 오류:', error);
    // 오류가 발생해도 기본 초기화는 시도
    try {
      window.fbq('init', META_PIXEL_ID);
      window.fbq('track', 'PageView');
    } catch (fallbackError) {
      console.error('Meta 픽셀 기본 초기화 실패:', fallbackError);
    }
  }
};

/**
 * 사용자 정보를 가져와서 Meta 픽셀 초기화
 * @param {Function} getInfoApi - 사용자 정보를 가져오는 API 함수
 */
export const initMetaPixelWithUserInfo = async (getInfoApi) => {
  try {
    if (getInfoApi) {
      const response = await getInfoApi();
      const userData = response && response.data ? response.data : null;
      
      if (userData) {
        const userInfo = {
          email: userData.email,
          name: userData.name,
          mobile: (userData.contactInfo && userData.contactInfo.mobile) ? userData.contactInfo.mobile : null,
        };
        await initMetaPixel(userInfo);
      } else {
        // 사용자 정보를 가져올 수 없는 경우 기본 초기화
        await initMetaPixel();
      }
    } else {
      // API 함수가 없는 경우 기본 초기화
      await initMetaPixel();
    }
  } catch (error) {
    console.error('사용자 정보 가져오기 실패, 기본 픽셀 초기화:', error);
    // 오류가 발생해도 기본 초기화는 시도
    await initMetaPixel();
  }
};

